import { PiListChecks } from 'react-icons/pi';
import type { Task } from 'src/api/generated';
import { getConversationTaskSummary } from 'src/modules/communications/utils/conversation-detail.util';
import Tag from 'src/modules/shared/components/atoms/tag';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

const ConversationHeaderTaskTooltipContent = ({ tasks }: { tasks: Task[] }) => {
  const { t } = useI18n();
  const { overdueTaskCount, upcomingTaskCount } =
    getConversationTaskSummary(tasks);
  const totalTaskCount = overdueTaskCount + upcomingTaskCount;

  return (
    <div className="flex flex-col gap-1">
      <Text size="paragraph" weight="medium">
        {totalTaskCount > 0
          ? t(
              totalTaskCount === 1
                ? 'communications.contactPanel.conversations.fields.taskCountSingular'
                : 'communications.contactPanel.conversations.fields.taskCountPlural',
              { count: totalTaskCount }
            )
          : t('communications.contactPanel.conversations.fields.viewTasks')}
      </Text>
      {totalTaskCount > 0 && (
        <div>
          {overdueTaskCount > 0 && (
            <Text size="small">
              {t('communications.conversationHeader.tasks.overdueCount', {
                count: overdueTaskCount,
              })}
            </Text>
          )}
          {upcomingTaskCount > 0 && (
            <Text size="small">
              {t('communications.conversationHeader.tasks.upcomingCount', {
                count: upcomingTaskCount,
              })}
            </Text>
          )}
        </div>
      )}
      <Text size="small">
        {t('communications.conversationHeader.tasks.clickToView')}
      </Text>
    </div>
  );
};

export const ConversationHeaderTaskTag = ({
  handleOpenTaskModal,
  isSmallScreen,
  tasks,
}: {
  handleOpenTaskModal: () => void;
  isSmallScreen: boolean;
  tasks: Task[];
}) => {
  const { t } = useI18n();
  const { overdueTaskCount, upcomingTaskCount } =
    getConversationTaskSummary(tasks);
  const totalTaskCount = overdueTaskCount + upcomingTaskCount;

  return (
    <Tooltip
      content={<ConversationHeaderTaskTooltipContent tasks={tasks} />}
      size="sm"
    >
      <Tag
        content={
          <div className="flex items-center gap-1">
            <PiListChecks className="h-4 w-4" />
            <Text size="tiny">
              {totalTaskCount > 0
                ? t(
                    totalTaskCount === 1
                      ? 'communications.contactPanel.conversations.fields.taskCountSingular'
                      : 'communications.contactPanel.conversations.fields.taskCountPlural',
                    { count: totalTaskCount }
                  )
                : t('communications.contactPanel.conversations.fields.tasks')}
            </Text>
          </div>
        }
        variant={
          overdueTaskCount > 0
            ? 'danger'
            : upcomingTaskCount > 0
              ? 'warning'
              : 'neutral'
        }
        appearance="outline"
        size="xs"
        onClick={handleOpenTaskModal}
        className={isSmallScreen ? 'flex-shrink-0' : ''}
      />
    </Tooltip>
  );
};
